// src/sections/Portfolio.tsx

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Blend, Cpu, MessageSquare } from 'lucide-react';

import PortfolioItem from '../components/PortfolioItem';
import Games from './Games';
import Tools from './Tools';
import Tutorials from './Tutorials';

type Filter = 'all' | 'digital twin' | 'artificial intelligence' | 'natural language';

// Each portfolio item should have a unique ID or something you can guarantee is unique.
// For instance, `title` might suffice if titles never repeat.
export type PortfolioData = {
  id: string;
  title: string;
  description: string;
  category: Filter;
  icon: React.ReactNode;
  link: string;
  size: 'small' | 'large';
  image?: string;
  video?: string;
};

export const portfolioData: PortfolioData[] = [
    {
      id: 'voxel-earth',
      title: 'Voxel Earth',
      description: 'Presented at SIGGRAPH 2024, a pipeline for voxelizing Google Earth 3D Tiles.',
      category: 'digital twin',
      icon: <Blend className="w-4 h-4" />,
      image: 'assets/images/voxel-earth.jpg',
      video: 'assets/projects/voxelearth/voxel-earth.mp4',
      link: 'projects/voxel-earth',
      size: 'large',
    },
    {
      id: 'fire-smoke-twin',
      title: 'Fire & Smoke Digital Twin',
      description: 'Published in CEUS, a real-time digital twin for predicting smoke in urban environments.',
      category: 'digital twin',
      icon: <Blend className="w-4 h-4" />,
      image: 'assets/images/voxel-earth.jpg',
      video: 'assets/projects/fireandsmokedigitaltwin/fire_smoke_twin.mp4',
      link: 'projects/fire-smoke-digital-twin',
      size: 'small',
    },
    {
      id: 'ai-feed',
      title: 'AI-FEED',
      description: '$750k NSF Convergence Accelerator project combatting food hunger with AI pantries.',
      category: 'artificial intelligence',
      icon: <Cpu className="w-4 h-4" />,
      image: 'assets/images/voxel-earth.jpg',
      video: 'assets/projects/aifeed/ai_feed.mp4',
      link: 'projects/ai-feed',
      size: 'small',
    },
    {
      id: 'crowdsourced-streetview',
      title: 'Crowdsourced Streetview',
      description: 'SIGGRAPH 2024, real-time updating of Google Streetview via social media.',
      category: 'digital twin',
      icon: <Blend className="w-4 h-4" />,
      image: 'assets/images/voxel-earth.jpg',
      video: 'assets/projects/crowdsourcedstreetview/crowdsourced_streetview.mp4',
      link: 'projects/crowdsourced-streetview',
      size: 'small',
    },
    {
      id: 'treefolio',
      title: 'Treefolio',
      description: 'Partnered with NYC4Parks, a recommendation tool for equitably planting trees and calculating shade.',
      category: 'digital twin',
      icon: <Blend className="w-4 h-4" />,
      video: 'assets/projects/treefolio/treefolio.mp4',
      link: 'projects/treefolio',
      size: 'small',
    },
    {
      id: 'computegpt',
      title: 'ComputeGPT',
      description: 'Pre-print on ArXiV, one of the first LLM code interpreters.',
      category: 'natural language',
      icon: <MessageSquare className="w-4 h-4" />,
      video: 'assets/projects/computegpt/computegpt.mp4',
      link: 'projects/computegpt',
      size: 'small',
    },
    {
      id: 'heat-risk-dashboard',
      title: 'Heat Risk Dashboard',
      description: 'Part of Cornell Initiative on Aging and Adaptation to Extreme Heat.',
      category: 'digital twin',
      icon: <Blend className="w-4 h-4" />,
      image: 'assets/images/heat_risk.png',
      link: 'projects/heat-risk-dashboard',
      size: 'small',
    },
    {
      id: 'austin-digital-twin',
      title: 'Austin Digital Twin',
      description: 'A digital twin for predicting real estate and showing crime and 911 calls.',
      category: 'digital twin',
      icon: <Blend className="w-4 h-4" />,
      image: 'assets/images/voxel-earth.jpg',
      video: 'assets/projects/austindigitaltwin/austin_digital_twin.mp4',
      link: 'projects/austin-digital-twin',
      size: 'small',
    },
    {
      id: 'recyclar',
      title: 'RecyclAR',
      description: '1st Place Niantic Labs Hackathon, AR app for recycling identification.',
      category: 'artificial intelligence',
      icon: <Cpu className="w-4 h-4" />,
      image: 'assets/projects/recyclar/recyclar.png',
      link: 'projects/recyclar',
      size: 'small',
    },
    {
      id: 'ut-austin-3d-virtual-tour',
      title: 'UT Austin 3D Virtual Tour',
      description: 'Explore the Forty Acres in 3D.',
      category: 'digital twin',
      icon: <Blend className="w-4 h-4" />,
      image: 'assets/projects/utexastour/logo.png',
      link: 'projects/ut-austin-3d-virtual-tour',
      size: 'small',
    },
    {
      id: 'indoor-air-temperature-model',
      title: 'Indoor Air Temperature Model',
      description: 'Predicting indoor air temperature with LSTM neural networks.',
      category: 'digital twin',
      icon: <Blend className="w-4 h-4" />,
      image: 'assets/projects/indoortemp/indoor.png',
      link: 'projects/indoor-temperature',
      size: 'small',
    },
    {
      id: 'datajourney',
      title: 'DataJourney',
      description: 'Graph-based data exploration via natural language queries.',
      category: 'natural language',
      icon: <MessageSquare className="w-4 h-4" />,
      image: 'assets/projects/datajourney/datajourney.png',
      link: 'projects/datajourney',
      size: 'small',
    },
    {
      id: 'central-documentation',
      title: 'Central Documentation',
      description: 'Centralized documentation portal for all projects and tools.',
      category: 'natural language',
      icon: <MessageSquare className="w-4 h-4" />,
      image: 'assets/tools/central_documentation/central_documentation.png',
      link: 'projects/central-documentation',
      size: 'small',
    },
    {
      id: 'meshtorrent',
      title: 'MeshTorrent',
      description:
        'Torrent-like distributed generation of forever free and public AI-generated 3D assets.',
      category: 'artificial intelligence',
      icon: <Cpu className="w-4 h-4" />,
      video: 'assets/projects/meshtorrent/loop.mp4',
      link: 'projects/meshtorrent',
      size: 'large',
    },    
    {
      id: 'city-contributor',
      title: 'City Contributor',
      description: 'Crowdsourced data archiving for municipalities.',
      category: 'digital twin',
      icon: <Blend className="w-4 h-4" />,
      image: 'assets/projects/citycontributor/citycontributor.svg',
      link: 'projects/city-contributor',
      size: 'small',
    },
    {
      id: 'urban-tessellation',
      title: 'Urban Tessellation',
      description: 'Discovering the best tessellation for urban environments.',
      category: 'digital twin',
      icon: <Blend className="w-4 h-4" />,
      image: 'assets/projects/urbantessellation/logo.png',
      link: 'projects/urban-tessellation',
      size: 'small',
    },
    {
      id: 'gitchat',
      title: 'GitChat',
      description: 'A git-based chat system, where every message is a commit.',
      category: 'natural language',
      icon: <MessageSquare className="w-4 h-4" />,
      image: 'assets/projects/gitchat/gitchat.png',
      link: 'projects/gitchat',
      size: 'small',
    },
    {
      id: 'ethics-ai',
      title: 'Ethics in AI',
      description:
        'Using games to train AI in moral reasoning.',
      category: 'artificial intelligence',
      icon: <Cpu className="w-4 h-4" />,
      image: 'assets/projects/ethicsinai/ethics.svg',
      link: 'projects/ethical-games',
      size: 'small',
    },  
    {
      id: 'neuroevolution-ppo-transfer',
      title: 'Neuroevolution PPO Transfer',
      description:
        'Zero-shot transfer from hybrid PPO-genetic algorithm to game, using genetic agents as experts to accelerate policy convergence.',
      category: 'artificial intelligence',
      icon: <Cpu className="w-4 h-4" />,
      video: 'assets/projects/transferlearning/genetic.mp4',
      link: 'projects/transfer-learning',
      size: 'large',
    },
    {
      id: 'rigtorrent',
      title: 'RigTorrent',
      description:
        'Distributed generation of fully rigged AI-generated 3D characters.',
      category: 'artificial intelligence',
      icon: <Cpu className="w-4 h-4" />,
      video: 'assets/projects/airigger/airigger.mp4',
      link: 'projects/rigtorrent',
      size: 'small',
    },  
    {
      id: 'llmcompression',
      title: 'LLM Compression',
      description:
        'Using LLMs to compress and decompress data.',
      category: 'artificial intelligence',
      icon: <Cpu className="w-4 h-4" />,
      image: 'assets/projects/llmcompression/llmcompression.png',
      link: 'projects/llm-compression',
      size: 'small',
    },  
];

const Portfolio: React.FC = () => {
  const [filter, setFilter] = useState<Filter>('all');
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      // remove '#' to get the element ID
      const targetId = location.hash.replace('#', '');
      const element = document.getElementById(targetId);

      if (element) {
        window.scrollTo({
          top: element.offsetTop,
          behavior: 'auto',
        });
      }
    }
  }, [location]);

  // Filter logic
  const filteredPortfolio = portfolioData.filter((item) => {
    if (filter === 'all') return true;
    return item.category === filter;
  });

  // Define filter buttons
  const filterButtons = [
    { label: 'All', value: 'all' },
    { label: 'digital twin', value: 'digital twin', icon: <Blend className="w-4 h-4" /> },
    { label: 'artificial intelligence', value: 'artificial intelligence', icon: <Cpu className="w-4 h-4" /> },
    { label: 'natural language', value: 'natural language', icon: <MessageSquare className="w-4 h-4" /> },
  ];

  return (
    <>
      <section id="portfolio" className="section">
        <div className="max-w-7xl mx-auto px-4 py-20">
          {/* Portfolio Label at Top Left */}
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-8">
            Portfolio
          </h1>

          {/* Filter Buttons */}
          <div className="flex justify-center mb-8 space-x-4 flex-wrap">
            {filterButtons.map((btn) => (
              <button
                key={btn.value}
                onClick={() => setFilter(btn.value as Filter)}
                className={clsx(
                  'filter-btn px-4 py-2 rounded-full flex items-center space-x-2 transition-colors mb-2',
                  {
                    'bg-blue-600 text-white hover:bg-blue-700':
                      filter === btn.value,
                    'bg-gray-200 dark:bg-gray-700 hover:bg-blue-600 hover:text-white':
                      filter !== btn.value,
                  }
                )}
              >
                {btn.icon}
                <span>{btn.label}</span>
              </button>
            ))}
          </div>

          {/* Portfolio Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {filteredPortfolio.map((item) => (
              <PortfolioItem
                key={item.id} // <-- Using a stable, unique key
                data={item}
                className={clsx(
                  'col-span-1',
                  item.size === 'large' && 'sm:col-span-2'
                )}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Other sections */}
      <Tools />
      <Games />
      <Tutorials />
    </>
  );
};

export default Portfolio;
